import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import api from '@/services/api';
import PageShell from '../../Components/PageShell';
import { STATUS_LABEL } from './subscriptionStatus';

// Convert a DB datetime to the value a datetime-local input expects (local time).
const toInputValue = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const OrgDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [level, setLevel] = useState('free');
    const [trialEndsAt, setTrialEndsAt] = useState('');
    const [saving, setSaving] = useState(false);
    const [creditsToAdd, setCreditsToAdd] = useState(10);
    const [addingCredits, setAddingCredits] = useState(false);

    const load = () =>
        api.get(`admin/organisations/${id}`)
            .then((d) => {
                setData(d);
                setLevel(d.org.subscription_level || 'free');
                setTrialEndsAt(toInputValue(d.org.trial_ends_at));
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));

    useEffect(() => {
        load();
    }, [id]);

    const fmt = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.put(`admin/organisations/${id}`, {
                subscription_level: level,
                trial_ends_at: trialEndsAt ? new Date(trialEndsAt).toISOString() : null,
            });
            message.success('Subscription updated');
            await load();
        } catch (err) {
            console.error('Error updating organisation:', err);
            message.error(err.response?.data?.error || 'Failed to update');
        } finally {
            setSaving(false);
        }
    };

    const handleAddCredits = async () => {
        try {
            setAddingCredits(true);
            await api.put(`admin/organisations/${id}`, { add_search_credits: Number(creditsToAdd) });
            message.success(`Added ${creditsToAdd} free searches`);
            await load();
        } catch (err) {
            message.error(err.response?.data?.error || 'Failed to add searches');
        } finally {
            setAddingCredits(false);
        }
    };

    const ent = data?.entitlements;
    const usage = data?.usage;
    const limits = ent?.limits;
    const fmtCap = (v) => (v === null ? '∞' : v);

    return (
        <PageShell wrap={false}>
            <div className="fs admin-org-detail">
                <button
                    type="button"
                    className="admin-org-back"
                    onClick={() => navigate('/admin/orgs')}
                >
                    ← Organisations
                </button>

                {error && (
                    <div className="admin-orgs-error">Error: {error}</div>
                )}

                {loading ? (
                    <div className="admin-orgs-loading">Loading…</div>
                ) : data && (
                    <>
                        <div className="admin-org-header">
                            <h1 className="admin-org-title">{data.org.name}</h1>
                            <span className="admin-org-sub">
                                {ent ? STATUS_LABEL[ent.status] || ent.status : data.org.subscription_level}
                            </span>
                        </div>
                        <div className="admin-org-meta">
                            <span>Joined {fmt(data.org.created_at)}</span>
                            <span>{data.members.length} member{data.members.length !== 1 ? 's' : ''}</span>
                            {ent?.status === 'trialing' && (
                                <span>
                                    Trial {data.org.trial_ends_at
                                        ? `ends ${fmt(data.org.trial_ends_at)}`
                                        : 'never expires'}
                                </span>
                            )}
                        </div>

                        {(data.org.stripe_customer_id || data.subscriptions?.length > 0) && (
                            <div className="team-table-wrap" style={{ marginTop: 24, padding: 20 }}>
                                <div className="section-head" style={{ marginBottom: 14 }}>
                                    <div>
                                        <span className="kicker">Billing</span>
                                        <h2 style={{ margin: 0 }}>Stripe.</h2>
                                    </div>
                                </div>
                                {data.org.stripe_customer_id && (
                                    <p style={{ margin: '0 0 14px', fontSize: 13 }}>
                                        Customer{' '}
                                        <a
                                            href={`https://dashboard.stripe.com/customers/${data.org.stripe_customer_id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {data.org.stripe_customer_id}
                                        </a>
                                    </p>
                                )}
                                {data.subscriptions?.length > 0 ? (
                                    <div className="admin-orgs-table-wrap">
                                        <table className="admin-orgs-table">
                                            <thead>
                                                <tr>
                                                    <th>Status</th>
                                                    <th>Seats</th>
                                                    <th>Started</th>
                                                    <th>Renews / ended</th>
                                                    <th>Subscription ID</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.subscriptions.map((sub) => (
                                                    <tr key={sub.id}>
                                                        <td>
                                                            <span className={`badge ${sub.status === 'active' ? 'success' : sub.status === 'canceled' || sub.status === 'unpaid' ? 'cold' : 'warm'}`}>
                                                                {sub.status}
                                                            </span>
                                                            {!!sub.cancel_at_period_end && <span style={{ marginLeft: 6, fontSize: 12, color: 'var(--muted)' }}>cancels at period end</span>}
                                                        </td>
                                                        <td>{sub.seats}</td>
                                                        <td className="admin-orgs-date">{fmt(sub.created_at)}</td>
                                                        <td className="admin-orgs-date">{fmt(sub.current_period_end)}</td>
                                                        <td className="admin-org-email">
                                                            {sub.stripe_subscription_id ? (
                                                                <a
                                                                    href={`https://dashboard.stripe.com/subscriptions/${sub.stripe_subscription_id}`}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    {sub.stripe_subscription_id}
                                                                </a>
                                                            ) : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)' }}>No Stripe subscription on record yet.</p>
                                )}
                            </div>
                        )}

                        {usage && limits && (
                            <div className="admin-org-meta" style={{ marginTop: 10 }}>
                                <span>Searches this month: {usage.searches}{limits.searchesPerMonth !== null ? ` / ${limits.searchesPerMonth}` : ''}</span>
                                <span>Free searches left: {usage.searchCredits}</span>
                                <span>Saved traders: {usage.savedTraders} / {fmtCap(limits.savedTraders)}</span>
                                <span>Saved searches: {usage.savedSearches} / {fmtCap(limits.savedSearches)}</span>
                                <span>Seats: {usage.seats} / {fmtCap(limits.seats)}</span>
                            </div>
                        )}

                        {ent?.status !== 'paid' && (
                            <div className="team-table-wrap" style={{ marginTop: 24, padding: 20 }}>
                                <div className="section-head" style={{ marginBottom: 14 }}>
                                    <div>
                                        <span className="kicker">Subscription</span>
                                        <h2 style={{ margin: 0 }}>Plan settings.</h2>
                                    </div>
                                </div>
                                <div className="profile-grid">
                                    <div className="form-field">
                                        <label htmlFor="org-level">Base plan</label>
                                        <select
                                            id="org-level"
                                            value={level}
                                            onChange={(e) => setLevel(e.target.value)}
                                        >
                                            <option value="free">Free searches</option>
                                            <option value="pro">Pro (complimentary)</option>
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label htmlFor="org-trial">Trial ends (your local time)</label>
                                        <input
                                            id="org-trial"
                                            type="datetime-local"
                                            value={trialEndsAt}
                                            onChange={(e) => setTrialEndsAt(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--muted)' }}>
                                    Leave the trial date empty for a never-expiring Pro trial.
                                    A past date drops the organisation to free searches
                                    (unless the base plan is Pro or they have a paid subscription).
                                </p>
                                <div className="form-field" style={{ maxWidth: 420 }}>
                                    <label htmlFor="org-credits">Give extra free searches</label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input
                                            id="org-credits"
                                            type="number"
                                            min={1}
                                            max={1000}
                                            value={creditsToAdd}
                                            onChange={(e) => setCreditsToAdd(e.target.value)}
                                            style={{ width: 100 }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handleAddCredits}
                                            disabled={addingCredits || !(Number(creditsToAdd) >= 1)}
                                        >
                                            {addingCredits ? 'Adding…' : 'Add searches'}
                                        </button>
                                    </div>
                                </div>
                                <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--muted)' }}>
                                    {usage ? `${usage.searchCredits} free searches left. ` : ''}
                                    Free searches only apply when the organisation isn't on Pro or a trial.
                                </p>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving…' : 'Save plan settings'}
                                    </button>
                                    {trialEndsAt && (
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setTrialEndsAt('')}
                                            disabled={saving}
                                        >
                                            Clear trial date
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="admin-orgs-table-wrap" style={{ marginTop: 24 }}>
                            <table className="admin-orgs-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>Role</th>
                                        <th>Joined</th>
                                        <th>Last seen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.members.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="admin-orgs-empty">No members</td>
                                        </tr>
                                    ) : (
                                        data.members.map((m) => (
                                            <tr key={m.user_id}>
                                                <td>{m.first_name} {m.last_name}</td>
                                                <td className="admin-org-email">{m.email}</td>
                                                <td className="admin-org-email">{m.phone || '—'}</td>
                                                <td className="admin-org-role">{m.role}</td>
                                                <td className="admin-orgs-date">{fmt(m.created_at)}</td>
                                                <td className="admin-orgs-date">
                                                    {m.last_seen_at ? fmt(m.last_seen_at) : 'not since tracking began'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </PageShell>
    );
};

export default OrgDetailPage;
